import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { WelcomeBoardService } from '../../../common/interfaces';
import { FileManager } from '../FileManager';
import type { FileType } from '../FileManager';
import { DraggableServiceItem } from './DraggableServiceItem';


interface ServicesSectionProps {
    services: WelcomeBoardService[];
    onServicesChange: (services: WelcomeBoardService[]) => void;
    clientId: string;
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({
    services,
    onServicesChange,
    clientId
}) => {
    const [localServices, setLocalServices] = useState<WelcomeBoardService[]>(services);
    const [fileManagerOpen, setFileManagerOpen] = useState<boolean>(false);
    const [fileManagerTarget, setFileManagerTarget] = useState<FileType>('image');
    const [selectedServiceIndex, setSelectedServiceIndex] = useState<number>(-1);
    const [expandedIndex, setExpandedIndex] = useState<number>(-1);
    const lastServiceRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setLocalServices(services);
    }, [services]);

    const addService = () => {
        const newServices = [...localServices, { htmlContent: '', title: '', isEnabled: true }];
        setLocalServices(newServices);
        onServicesChange(newServices);

        // Expand the newly added service
        const newIndex = newServices.length - 1;
        setExpandedIndex(newIndex);

        // Scroll to the new service after a brief delay to allow rendering
        setTimeout(() => {
            if (lastServiceRef.current) {
                lastServiceRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
    };

    const removeService = (index: number) => {
        const updatedServices = localServices.filter((_, i) => i !== index);
        setLocalServices(updatedServices);
        onServicesChange(updatedServices);

        // Clear expansion if we deleted the expanded item
        if (expandedIndex === index) {
            setExpandedIndex(-1);
        } else if (expandedIndex > index) {
            // Adjust expanded index if we deleted an item before it
            setExpandedIndex(expandedIndex - 1);
        }
    };

    const updateService = (index: number, service: WelcomeBoardService) => {
        const updatedServices = [...localServices];
        updatedServices[index] = service;
        setLocalServices(updatedServices);
        onServicesChange(updatedServices);
    };

    const moveService = useCallback((dragIndex: number, hoverIndex: number) => {
        const updatedServices = [...localServices];
        const draggedService = updatedServices[dragIndex];

        // Remove from old position
        updatedServices.splice(dragIndex, 1);
        // Insert at new position
        updatedServices.splice(hoverIndex, 0, draggedService);

        setLocalServices(updatedServices);
        onServicesChange(updatedServices);

        // Update expanded index if needed
        if (expandedIndex === dragIndex) {
            setExpandedIndex(hoverIndex);
        } else if (dragIndex < expandedIndex && hoverIndex >= expandedIndex) {
            setExpandedIndex(expandedIndex - 1);
        } else if (dragIndex > expandedIndex && hoverIndex <= expandedIndex) {
            setExpandedIndex(expandedIndex + 1);
        }
    }, [localServices, expandedIndex, onServicesChange]);

    const handleExpand = (index: number) => {
        setExpandedIndex(expandedIndex === index ? -1 : index);
    };

    const openFileManager = (target: FileType, serviceIndex: number) => {
        setFileManagerTarget(target);
        setSelectedServiceIndex(serviceIndex);
        setFileManagerOpen(true);
    };

    const handleFileSelect = (filePath: string) => {
        if (selectedServiceIndex >= 0) {
            const updatedService = { ...localServices[selectedServiceIndex] };

            if (fileManagerTarget === 'image') {
                updatedService.imageFile = filePath;
            } else if (fileManagerTarget === 'video') {
                updatedService.videoFile = filePath;
            }

            updateService(selectedServiceIndex, updatedService);
        }
        setFileManagerOpen(false);
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Services {localServices.length > 0 && `(${localServices.length})`}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={addService}
                        >
                            Add Service
                        </Button>
                    </Box>

                    {localServices.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No services added yet. Click "Add Service" to get started.
                        </Typography>
                    )}

                    {localServices.map((service, index) => (
                        <div
                            key={index}
                            ref={index === localServices.length - 1 ? lastServiceRef : null}
                        >
                            <DraggableServiceItem
                                service={service}
                                index={index}
                                expanded={expandedIndex === index}
                                onExpand={handleExpand}
                                onUpdate={updateService}
                                onDelete={removeService}
                                onMove={moveService}
                                onOpenFileManager={openFileManager}
                                clientId={clientId}
                            />
                        </div>
                    ))}
                </CardContent>

                {/* File Manager */}
                <FileManager
                    open={fileManagerOpen}
                    onClose={() => setFileManagerOpen(false)}
                    clientId={clientId}
                    onFileSelect={handleFileSelect}
                    fileType={fileManagerTarget}
                />
            </Card>
        </DndProvider>
    );
};